// Transaction Wizard — the `grid` step's spreadsheet gestures, as pure
// functions (docs/plans/transaction-wizard.md §6.5): what a multi-row paste
// and a fill-down WRITE, and how wide each column is drawn. GridStep turns a
// plan into ONE `setCells` call, so every written cell goes through the same
// path typing does (the value, then that column's item_change, in order).
//
// Cells are addressed by POSITION in what the grid shows: `rows` are the
// record's line indexes in display order (after `row_filter`), `columns` the
// EDITABLE columns in display order — a paste fills editable columns only,
// skipping the read-only ones between them, as the keyboard does.

/** One cell a plan writes. `line` is a record line index, or `{add: n}` for
 *  the n-th row the plan adds (0-based, in order). A paste writes the
 *  clipboard TEXT (the grid coerces it by the column's type, as typing it
 *  would); a fill-down copies the source cell's value as it is. */
export interface GridCellWrite {
  line: number | { add: number }
  column: string
  value: unknown
}

export interface GridWritePlan {
  writes: GridCellWrite[]
  /** New rows to add first (only when the step allows adding). */
  addRows: number
  /** Clipboard cells that had nowhere to go (past the last column, or past
   *  the last row of a grid that may not grow). */
  dropped: number
}

/**
 * Clipboard text → rows of cells, as a spreadsheet copies them: TAB between
 * cells, a newline between rows, one trailing newline ignored, and a cell in
 * double quotes may hold tabs / newlines / doubled quotes (Excel and Google
 * Sheets quote a cell that contains a line break).
 */
export function parseClipboardGrid(text: string): string[][] {
  const src = String(text ?? '').replace(/\r\n?/g, '\n')
  if (!src) return []
  const rows: string[][] = []
  let row: string[] = []
  let cell = ''
  let i = 0
  let atCellStart = true
  while (i < src.length) {
    const ch = src[i]
    if (atCellStart && ch === '"') {
      // A quoted cell: up to the closing quote ("" is a literal quote).
      let j = i + 1
      let val = ''
      let closed = false
      while (j < src.length) {
        if (src[j] === '"') {
          if (src[j + 1] === '"') { val += '"'; j += 2; continue }
          closed = true
          j += 1
          break
        }
        val += src[j]
        j += 1
      }
      // Only a quote that closes right before a separator is CSV-style
      // quoting; anything else is a cell that merely starts with a quote.
      if (closed && (j >= src.length || src[j] === '\t' || src[j] === '\n')) {
        cell = val
        i = j
        atCellStart = false
        continue
      }
    }
    atCellStart = false
    if (ch === '\t') {
      row.push(cell)
      cell = ''
      atCellStart = true
    } else if (ch === '\n') {
      row.push(cell)
      rows.push(row)
      row = []
      cell = ''
      atCellStart = true
    } else {
      cell += ch
    }
    i += 1
  }
  // The text did not end with a newline: the last row is still open.
  if (!atCellStart || cell !== '' || row.length) {
    row.push(cell)
    rows.push(row)
  }
  return rows
}

/** Whether pasted text is a BLOCK (several cells) rather than one value —
 *  a single value pastes natively into the focused input. */
export function isMultiCellText(text: string): boolean {
  const t = String(text ?? '').replace(/\r\n?/g, '\n').replace(/\n$/, '')
  return t.includes('\t') || t.includes('\n')
}

/**
 * What pasting `data` at (`startRow`, `startCol`) writes. `rows` are the
 * visible line indexes, `columns` the editable columns (both in display
 * order); `locked(line, column)` marks a cell the row protects. Rows past the
 * last are added when `allowAdd`, otherwise dropped.
 */
export function planPaste(opts: {
  data: string[][]
  rows: number[]
  columns: string[]
  startRow: number
  startCol: number
  allowAdd: boolean
  locked?: (line: number, column: string) => boolean
}): GridWritePlan {
  const { data, rows, columns, allowAdd, locked } = opts
  const startRow = Math.max(0, opts.startRow)
  const startCol = Math.max(0, opts.startCol)
  const writes: GridCellWrite[] = []
  let addRows = 0
  let dropped = 0
  data.forEach((cells, r) => {
    const pos = startRow + r
    let line: number | { add: number } | null = null
    if (pos < rows.length) line = rows[pos]
    else if (allowAdd) {
      line = { add: pos - rows.length }
      addRows = Math.max(addRows, pos - rows.length + 1)
    }
    cells.forEach((text, c) => {
      const column = columns[startCol + c]
      if (line === null || column === undefined) { dropped += 1; return }
      if (typeof line === 'number' && locked?.(line, column)) { dropped += 1; return }
      writes.push({ line, column, value: text })
    })
  })
  return { writes, addRows, dropped }
}

/**
 * Fill-down: the focused cell's value copied into the SELECTED rows below it
 * (`toRow`, a position in `rows`, when a range is selected), else into every
 * row after it. Locked cells are left alone.
 */
export function planFillDown(opts: {
  rows: number[]
  column: string
  fromRow: number
  toRow?: number | null
  value: unknown
  locked?: (line: number, column: string) => boolean
}): GridWritePlan {
  const { rows, column, value, locked } = opts
  const from = Math.max(0, opts.fromRow)
  const to = opts.toRow != null ? Math.min(opts.toRow, rows.length - 1) : rows.length - 1
  const writes: GridCellWrite[] = []
  let dropped = 0
  for (let pos = from + 1; pos <= to; pos++) {
    const line = rows[pos]
    if (locked?.(line, column)) { dropped += 1; continue }
    writes.push({ line, column, value })
  }
  return { writes, addRows: 0, dropped }
}

/**
 * Column widths that fit what the grid holds, in `ch`: the longer of the
 * heading and the widest value seen, bounded so one long note does not push
 * every other column off screen. Editable columns keep room to type.
 */
export function fitColumnWidths(
  columns: Array<{ key: string; heading: string; editable: boolean }>,
  values: (key: string) => string[],
  bounds: { min?: number; max?: number; editableMin?: number } = {},
): Record<string, number> {
  const min = bounds.min ?? 6
  const max = bounds.max ?? 36
  const editableMin = bounds.editableMin ?? 10
  const out: Record<string, number> = {}
  for (const c of columns) {
    let widest = String(c.heading || '').length
    for (const v of values(c.key)) widest = Math.max(widest, String(v ?? '').length)
    const floor = c.editable ? editableMin : min
    // + padding for the cell editor's chrome (a select arrow, a date icon).
    out[c.key] = Math.min(max, Math.max(floor, widest + (c.editable ? 4 : 2)))
  }
  return out
}
